import {
  Brain,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Gift,
  LayoutDashboard,
  type LucideIcon,
  Search,
  ShieldCheck,
  Trophy,
  User,
  Users,
  Workflow,
} from 'lucide-react';
import { ADMIN_PERMISSIONS } from '@/features/admin/admin-tabs';

/**
 * Top-level navigation model.
 *
 * An item is shown when ALL THREE gates pass:
 *
 * - `permissions` — any-of; undefined means "no permission needed". The sidebar
 *   filters with the same effective permissions the backend enforces, so the nav
 *   can never offer a section the user would be 403'd from.
 * - `audience` — which account kind the item is for; undefined means everyone.
 * - `requiresTenant` — the section reads tenant-scoped data, so it is unusable
 *   without a tenant in context.
 *
 * The audience gate exists because permissions alone cannot express the applicant
 * surface: applicants hold NO permissions by design, so "no permission needed"
 * would put My applications and My profile in front of every staff member too.
 * Account kind is the real distinction (`identity.account.kind` is exclusive in
 * v1 — one person is STAFF or APPLICANT, never both).
 *
 * The tenant gate exists because permission is necessary but NOT sufficient for
 * a tenant-scoped section. A platform Super Admin holds the entire catalogue
 * (the seed grants SUPER_ADMIN every permission) while having no tenant, and so
 * did multi-tenant staff before selecting one — `Ctx.tenantId` is null in both
 * cases. Those principals were being offered Awards, Applications, Pipeline,
 * Approvals, Team and Fulfilment, and every one of them answered 400
 * TENANT_REQUIRED: the nav was making a promise the API refuses. A section that
 * cannot work without a tenant is hidden until there is one.
 */
export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  permissions?: string[];
  /** Restrict to one account kind. Undefined = shown to every signed-in user. */
  audience?: 'STAFF' | 'APPLICANT';
  /** Hide unless a tenant is in context — the section's API is tenant-scoped. */
  requiresTenant?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/', labelKey: 'nav.dashboard' /* Dashboard */, icon: LayoutDashboard, end: true },

  // --- Applicant surface -------------------------------------------------
  // Deliberately above the staff items: for the people who see these, they are
  // the whole application, not a section of it.
  { to: '/opportunities', labelKey: 'nav.browse' /* Open opportunities */, icon: Search, audience: 'APPLICANT' },
  { to: '/my/applications', labelKey: 'nav.myApplications' /* My applications */, icon: FileText, audience: 'APPLICANT' },
  { to: '/my/profile', labelKey: 'nav.myProfile' /* My profile */, icon: User, audience: 'APPLICANT' },

  // --- Staff surface -----------------------------------------------------
  // Every item here is tenant-scoped: an award, its queue, its panels and its
  // team all belong to one tenant, and their endpoints answer TENANT_REQUIRED
  // without one. Even the two that start from an award id are gated, because the
  // award list they start from is itself refused.
  { to: '/awards', labelKey: 'nav.awards' /* Awards */, icon: Trophy, permissions: ['awards.create'], requiresTenant: true },
  { to: '/pre-assessments', labelKey: 'nav.preAssessments' /* Assessments */, icon: Brain, permissions: ['awards.update'], requiresTenant: true },
  { to: '/applications', labelKey: 'nav.applications' /* Applications */, icon: FileText, permissions: ['applications.view_all'], requiresTenant: true },
  // Beside Applications, not under Awards: the queue and the pipeline are the
  // two halves of running a competition, while /awards is where one is designed.
  { to: '/pipeline', labelKey: 'nav.pipeline' /* Award progress */, icon: Workflow, permissions: ['applications.view_all'], requiresTenant: true },
  // `scores.submit` only: this is the evaluator's OWN list, so someone holding
  // scores.view_all but not scores.submit has nothing to see here — offering it
  // to them would be a nav item that leads straight to a 403.
  { to: '/evaluations', labelKey: 'nav.evaluations' /* Scoring */, icon: ClipboardCheck, permissions: ['scores.submit'], requiresTenant: true },
  // `approvals.decide` only. The page is the approver's own queue and
  // delegations, both of which the API guards with that permission. TENANT_ADMIN
  // holds `approvals.configure` (gate plans live on Award progress) but not
  // `approvals.decide`, so offering this item to them was a nav link that 403'd
  // the queue the moment it opened.
  { to: '/approvals', labelKey: 'nav.approvals' /* Approvals */, icon: CheckCircle2, permissions: ['approvals.decide'], requiresTenant: true },
  { to: '/fulfilment', labelKey: 'nav.fulfilment' /* Fulfilment */, icon: Gift, permissions: ['fulfilment.view'], requiresTenant: true },
  { to: '/team', labelKey: 'nav.team' /* Team */, icon: Users, permissions: ['staff.view'], requiresTenant: true },
  // Same list the /admin route guard uses — one source, so the nav can never
  // offer the console to someone the router would then 403.
  { to: '/admin', labelKey: 'nav.admin' /* Settings */, icon: ShieldCheck, permissions: ADMIN_PERMISSIONS },
];

/**
 * Does this item's audience gate admit `kind`?
 *
 * An ungated item is for everyone; a gated one needs an exact match. An
 * unrecognised kind therefore sees the ungated items and whatever its
 * permissions earn — never an applicant surface it has no profile for.
 */
export function matchesAudience(item: NavItem, kind: string | undefined): boolean {
  return item.audience === undefined || item.audience === kind;
}

/**
 * Does this item's tenant gate admit `tenantId`?
 *
 * Only asks about the tenant. An ungated item always passes; a gated one needs a
 * tenant in context. Note this is NOT "are they staff" — a platform Super Admin
 * is the most privileged principal there is and still has no tenant, which is
 * exactly the case the gate exists for.
 */
export function matchesTenant(item: NavItem, tenantId: string | null | undefined): boolean {
  return item.requiresTenant !== true || (tenantId !== null && tenantId !== undefined);
}

/**
 * Pages that live under one section's path but belong to another. Panel 360
 * sits at `/awards/:awardId/rounds/…` because it is addressed by award and
 * round, yet it is a page of running a round — the work Award progress (the
 * pipeline and its judging setup) is for, and where its breadcrumb starts.
 */
const SECTION_ALIASES: ReadonlyArray<{ pattern: RegExp; to: string }> = [{ pattern: /^\/awards\/[^/]+\/rounds(\/|$)/, to: '/pipeline' }];

/**
 * The `to` of the nav section a path belongs to: an alias first, then the
 * longest item prefix; the dashboard (`/`) only on an exact match. Null when
 * the path belongs to no section. One answer for the sidebar's highlight and
 * the top bar's title, so the two can never disagree.
 */
export function activeNavTo(pathname: string, items: readonly NavItem[] = NAV_ITEMS): string | null {
  const alias = SECTION_ALIASES.find((a) => a.pattern.test(pathname));
  if (alias) return alias.to;
  let best: string | null = null;
  for (const item of items) {
    const hit = item.to === '/' || item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (hit && (best === null || item.to.length > best.length)) best = item.to;
  }
  return best;
}
